import React, { useState, useEffect } from "react"
import { Model, ComputedUpdater } from 'survey-core'
import { Survey } from "survey-react-ui";
import { PlainLight } from "survey-core/themes";
import PanelHeader from "components/PanelHeader/PanelHeader.jsx";
import { useParams, Prompt } from "react-router-dom";
import { Modal, ModalBody, ModalFooter, Button, Card, CardBody, Row, Col } from "reactstrap"
import AgentService from "core/api/AgentService";

const formatBuildingAddress = (building) => {
    if (!building) return ""

    return [building.line1, building.line2, building.city, building.state, building.postalCode]
        .filter(Boolean)
        .join(", ")
}

const escapeHtml = (text) => String(text ?? "").replace(
    /[&<>"']/g,
    character => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;"
    })[character]
)

export default function ServiceRequestReport() {
    const [reportData, setReportData] = useState();
    const [survey, setSurvey] = useState();
    const [reportSaved, setReportSaved] = useState(false);
    const [terminateReportPopup, setTerminateReportPopup] = useState(false);
    const [terminationReason, setTerminationReason] = useState("")
    const [saveBeforeLeave, setSaveBeforeLeave] = useState(false)
    const [reportTerminated, setReportTerminated] = useState(false)
    const { id } = useParams();

    const getSurveyJson = (elevatorCompanyName) => {
        return {
            clearInvisibleValues: "onHidden",
            completeText: "Send Report",
            completedHtml: "<b><h2>Your Report Was Submitted</h2></b>",
            requiredText: "",
            showQuestionNumbers: false,
            showTimerPanel: "top",
            showTimerPanelMode: "survey", 
            pages: [
                {
                    elements: [
                        {
                            type: "panel",
                            name: "introductionPanel",
                            title: "Introduction",
                            elements: [
                                {
                                    type: "html",
                                    name: "introduction",
                                    html: `<p><b>${elevatorCompanyName ? escapeHtml(elevatorCompanyName) + ", " : ""}How can I help you?</b></p>`
                                }
                            ]
                        },
                        {
                            name: "callerName",
                            title: "Caller's Name",
                            type: "text",
                            isRequired: true
                        },
                        {
                            name: "callbackNumber",
                            title: "Phone Number for Callback",
                            type: "text",
                            inputType: "tel",
                            isRequired: true
                        },
                        {
                            name:"reasonForCall",
                            title:"Reason For Call",
                            type: "dropdown",
                            isRequired: true,
                            choices:["Broken Button", "Elevator not working properly", "Shut down"],
                            showOtherItem: true,
                            storeOthersAsComment: false,
                            otherText:"Other",
                            otherErrorText: "Please specify the reason for the call",
                        },
                        {
                            name: "buildingId",
                            title: "Building Address",
                            type: "dropdown",
                            choicesLazyLoadEnabled: true,
                            choicesLazyLoadPageSize: 25,
                            //isRequired: true
                        },
                        {
                            name: "manualAddress",
                            title: "If you can't find the address, enter it manually",
                            type: "text",
                            visibleIf: "{buildingId} empty",
                        //isRequired: true
                        },
                        {
                            name: "isUrgent",
                            title: "Is this service request urgent?",
                            type: "radiogroup",
                            choices: ["Yes", "No"],
                            isRequired: true
                        },
                        {
                            name: "afterHoursApproval",
                            title: "Does the caller approve after-hours rates?",
                            type: "radiogroup",
                            choices: ["Yes", "No"],
                            visibleIf: "{isUrgent} = 'Yes' and {buildingHasAfterHoursContract} = 'No'",
                            isRequired: true
                        },
                    ]
                }
            ]
        }
    }

    const terminateReport = async () => {
        await saveReport(survey, true, null, true)
        setReportTerminated(true)
        setTerminateReportPopup(false)
    }    
    
    const saveReport = async (sender, incomplete = false, reportInfo, terminate = false) => {
        let data = reportInfo? reportInfo : reportData
        const timeSpent = sender.timeSpent < 60 ? 1 : Math.trunc(sender.timeSpent / 60)

        const buildingId = sender.getValue("buildingId") || null

        data = {
            ...data,
            callLength: timeSpent,
            surveyResults: { ...sender.data },
            buildingId: buildingId,
            buildingAddress: buildingId ? null : sender.getValue("manualAddress") || null,
        }

        if (terminate) {
            data = {
                ...data,
                surveyResults: { ...data.surveyResults, terminationReason },
                dateTimeSubmitted: new Date(),
                status: "terminated",
            }
        } 
        
        if (!incomplete && !terminate) {
            data = {
                ...data,
                dateTimeSubmitted: new Date(),
                status: "submitted",
                resolution: "Service Request Sent",
            }
        }

        const savedCall = await AgentService.saveServiceRequestReport(data)
        setReportData(savedCall)

        if (data.status === "submitted" || data.status === "terminated") {
            setSaveBeforeLeave(false)

            if (data.status === "submitted") {
                await AgentService.sendCallSummaryReport(savedCall)
            }
        }


    }

    const initializeReport = (data, building) => {
        const surveyModel = new Model(getSurveyJson(data?.elevatorCompanyName))
        const buildingLabels = {}
        const buildingsById = {}

        if (building) {
            buildingLabels[building.id] = formatBuildingAddress(building)
            buildingsById[building.id] = building
        }

        const surveyData = { ...(data.surveyResults ?? {}) }
        if (data.buildingId) {
            surveyData.buildingId = data.buildingId
            surveyData.manualAddress = null
        } else if (data.buildingAddress) {
            surveyData.buildingId = null
            surveyData.manualAddress = data.buildingAddress
        }

        // Supply the saved building's address label for the lazy-loaded dropdown.
        surveyModel.onGetChoiceDisplayValue.add((sender, options) => {
            if (options.question.name !== "buildingId") return

            options.setItems(options.values.map(value => buildingLabels[value] || value))
        })

        surveyModel.data = surveyData
        surveyModel.getQuestionByName("buildingId").readOnly = Boolean(data?.elevatorId && data?.buildingId)

        if (data.status === "answered") {
            surveyModel.mode = "edit"
            setSaveBeforeLeave(true)

            surveyModel.addNavigationItem({
                id: "sj-save-incomplete-survey",
                title: "Save Incomplete Form",
                action: async () => { await saveReport(surveyModel, true, data); setReportSaved(true) },
                css: "nav-button",
                innerCss: "sd-btn nav-input",
                visibleIndex: 50
            });

            surveyModel.addNavigationItem({
                id: "sj-terminate-survey",
                title: "Terminate Call",
                action: () => { setTerminateReportPopup(true) },
                css: "nav-button",
                innerCss: "sd-btn nav-input",
                visibleIndex: 52,
                visible: new ComputedUpdater(() => surveyModel.state !== "completed")
            });

            const updateContractStatus = (buildingId) => {
                const selectedBuilding = buildingsById[buildingId]
                surveyModel.setValue("buildingHasAfterHoursContract", selectedBuilding?.hasAfterHoursServiceContract ? "Yes" : "No")
            }

            // Keep saved approval only if the building's contract status is unchanged.
            updateContractStatus(surveyData.buildingId)
            if (surveyModel.getValue("buildingHasAfterHoursContract") !== surveyData.buildingHasAfterHoursContract) {
                surveyModel.clearValue("afterHoursApproval")
            }

            surveyModel.onValueChanged.add((sender, options) => {
                if (options.name === "buildingId") {
                    sender.clearValue("afterHoursApproval")
                    updateContractStatus(options.value)
                }
            })

            //set buildings in dropdown with lazy loading
            surveyModel.onChoicesLazyLoad.add(async (sender, options) => {
                if (options.question.name !== "buildingId") {
                    return;
                }

                const search = options.filter || "";
                const pageSize = options.take || 50;
                const page = Math.floor((options.skip || 0) / pageSize) + 1;
                
                const result = await AgentService.getBuildingsByAddress(search, page, pageSize, data?.elevatorCompanyId);
                

                const choices = result?.model.map(building => {
                    const address = formatBuildingAddress(building)
                    buildingLabels[building.id] = address
                    buildingsById[building.id] = building

                    return {
                        value: building.id,
                        text: address || building.id
                    }
                });

                options.setItems(choices, choices.length);
            
            });


        } else {
            surveyModel.mode = "display"
            setSaveBeforeLeave(false)
        }

        surveyModel.applyTheme(PlainLight);
        surveyModel.timeSpent = data.callLength * 60

        setSurvey(surveyModel)
    }

    useEffect(() => {
        async function fetchData() {
            const reportResults = await AgentService.getServiceReportData(id)
            setReportData(reportResults?.call)

            initializeReport(reportResults?.call, reportResults?.building)
        }

        if (id) fetchData();
    }, [id])


    return (
        <>
            <PanelHeader content={
                <div className="header text-center">
                    <h1 className="title">
                        Service Request Report
                    </h1>
                    {reportData &&
                        <h2 className="title">
                            {reportData.elevatorCompanyName}
                        </h2>
                    }
                </div>
            } />

            <Modal isOpen={reportSaved} centered={true}>
                <ModalBody style={{ justifyContent: "center", alignContent: "center", textAlign: "center" }}>
                    <div className="title">Form was Saved</div>
                    <i className="las la-clipboard-check" style={{ fontSize: 60 }}></i>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setReportSaved(false)} style={{ margin: "auto", display: "block" }}>OK</Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={terminateReportPopup} centered={true}>
                <ModalBody style={{ justifyContent: "center", alignContent: "center", textAlign: "center" }}>
                    <div className="title">Terminate Report</div>
                    <i className="las la-phone-slash" style={{ fontSize: 60 }}></i>
                    <div>Enter termination reason</div>
                    <textarea onChange={(event) => setTerminationReason(event.target.value)} style={{ width: "65%" }}></textarea>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setTerminateReportPopup(false)} style={{ margin: "auto", display: "block" }}>Cancel</Button>
                    <Button color="primary" onClick={terminateReport} style={{ margin: "auto", display: "block" }}>Terminate</Button>
                </ModalFooter>
            </Modal>

            <div className="content">
                <Row>
                    <Col md="12">
                        <Card>
                            <CardBody>
                                {reportTerminated ?
                                    <h2 style={{ paddingTop: 20, fontWeight: "bold", textAlign: "center" }}>Your Report Was Terminated</h2>
                                    : 
                                    survey && <Survey model={survey} onComplete={() => saveReport(survey)} />
                                }
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                <Prompt when={saveBeforeLeave} message={() => {
                    saveReport(survey, true)
                    return true
                }} />
            </div>
        </>
    );
}
